import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import type { Database } from "@/integrations/supabase/types";

type VehicleCategory = Database["public"]["Enums"]["vehicle_category"];
type FuelType = Database["public"]["Enums"]["fuel_type"];
type AcquisitionType = Database["public"]["Enums"]["acquisition_type"];
type VehicleStatus = Database["public"]["Enums"]["vehicle_status"];
type VehicleDocumentType = Database["public"]["Enums"]["vehicle_document_type"];
type ComplianceStatus = Database["public"]["Enums"]["compliance_status"];

export interface Vehicle {
  id: string;
  user_id: string;
  organization_id: string | null;
  license_plate: string;
  brand: string;
  model: string;
  category: VehicleCategory;
  first_registration_date: string | null;
  vin: string | null;
  fuel_type: FuelType | null;
  current_mileage: number;
  last_mileage_update: string | null;
  acquisition_type: AcquisitionType | null;
  acquisition_date: string | null;
  status: VehicleStatus;
  compliance_score: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface VehicleDocument {
  id: string;
  vehicle_id: string;
  document_id: string | null;
  doc_type: VehicleDocumentType;
  label: string | null;
  valid_from: string | null;
  expires_at: string | null;
  status: ComplianceStatus;
  notes: string | null;
  created_at: string;
  updated_at: string;
  linked_document?: {
    id: string;
    title: string;
    file_name: string;
    file_path: string;
  } | null;
}

export interface Driver {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  license_number: string | null;
  license_expiry: string | null;
  license_categories: string[];
  license_document_id: string | null;
  access_token: string;
  status: Database["public"]["Enums"]["driver_status"];
  invitation_sent_at: string | null;
  portal_last_access_at: string | null;
  created_at: string;
}

export type VehicleInsert = Omit<Vehicle, "id" | "user_id" | "created_at" | "updated_at" | "compliance_score" | "current_mileage" | "last_mileage_update">;

// Documents obligatoires par catégorie de véhicule
export const VEHICLE_DOC_TYPES: Record<VehicleDocumentType, {
  label: string;
  mandatory: { vl: boolean; vu: boolean; pl: boolean; two_wheels: boolean };
  validityDays: number | null;
  alertDays: number;
}> = {
  carte_grise: { 
    label: "Carte grise", 
    mandatory: { vl: true, vu: true, pl: true, two_wheels: true }, 
    validityDays: null, 
    alertDays: 0 
  },
  assurance: { 
    label: "Attestation d'assurance", 
    mandatory: { vl: true, vu: true, pl: true, two_wheels: true }, 
    validityDays: 365, 
    alertDays: 30 
  },
  controle_technique: { 
    label: "Contrôle technique", 
    mandatory: { vl: true, vu: true, pl: true, two_wheels: false }, 
    validityDays: 730, 
    alertDays: 60 
  },
  crit_air: { 
    label: "Vignette Crit'Air", 
    mandatory: { vl: true, vu: true, pl: true, two_wheels: true }, 
    validityDays: null, 
    alertDays: 0 
  },
  leasing_contract: { 
    label: "Contrat leasing/LOA", 
    mandatory: { vl: false, vu: false, pl: false, two_wheels: false }, 
    validityDays: null, 
    alertDays: 90 
  },
  permis_conducteur: { 
    label: "Permis du conducteur", 
    mandatory: { vl: true, vu: true, pl: true, two_wheels: true }, 
    validityDays: 5475, 
    alertDays: 90 
  },
  attestation_entretien: { 
    label: "Carnet d'entretien", 
    mandatory: { vl: false, vu: false, pl: true, two_wheels: false }, 
    validityDays: 365, 
    alertDays: 30 
  },
  chronotachygraphe: { 
    label: "Carte chronotachygraphe", 
    mandatory: { vl: false, vu: false, pl: true, two_wheels: false }, 
    validityDays: 1825, 
    alertDays: 90 
  },
  carte_carburant: { 
    label: "Carte carburant", 
    mandatory: { vl: false, vu: false, pl: false, two_wheels: false }, 
    validityDays: null, 
    alertDays: 0 
  },
  pv_infraction: { 
    label: "PV d'infraction", 
    mandatory: { vl: false, vu: false, pl: false, two_wheels: false }, 
    validityDays: null, 
    alertDays: 0 
  },
  other: { 
    label: "Autre document", 
    mandatory: { vl: false, vu: false, pl: false, two_wheels: false }, 
    validityDays: null, 
    alertDays: 0 
  },
};

export const VEHICLE_CATEGORIES: Record<VehicleCategory, string> = {
  vl: "Véhicule léger",
  vu: "Véhicule utilitaire",
  pl: "Poids lourd",
  two_wheels: "2 roues",
  other: "Autre",
};

export const FUEL_TYPES: Record<string, string> = {
  gasoline: "Essence",
  diesel: "Diesel",
  electric: "Électrique",
  hybrid: "Hybride",
  lpg: "GPL",
  other: "Autre",
};

export const ACQUISITION_TYPES: Record<string, string> = {
  owned: "Propriété",
  leasing: "Leasing",
  loa: "LOA",
  lld: "LLD",
  rental: "Location courte durée",
};

export function getRequiredDocs(category: VehicleCategory): VehicleDocumentType[] {
  return (Object.entries(VEHICLE_DOC_TYPES) as [VehicleDocumentType, typeof VEHICLE_DOC_TYPES[VehicleDocumentType]][])
    .filter(([_, config]) => config.mandatory[category])
    .map(([key]) => key);
}

export function useVehicles() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: vehicles = [], isLoading, error } = useQuery({
    queryKey: ["vehicles", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from("vehicles")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Vehicle[];
    },
    enabled: !!user?.id,
  });

  const createVehicle = useMutation({
    mutationFn: async (vehicle: VehicleInsert) => {
      if (!user?.id) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("vehicles")
        .insert({
          ...vehicle,
          user_id: user.id,
          compliance_score: 0,
          current_mileage: 0,
        })
        .select()
        .single();

      if (error) throw error;
      
      // Create mandatory documents for this vehicle
      const requiredDocs = getRequiredDocs(vehicle.category);
      if (requiredDocs.length > 0) {
        const docsToCreate = requiredDocs.map(docType => ({
          vehicle_id: data.id,
          doc_type: docType,
          label: VEHICLE_DOC_TYPES[docType].label,
          status: "missing" as ComplianceStatus,
        }));
        
        const { error: docsError } = await supabase.from("vehicle_documents").insert(docsToCreate);
        if (docsError) {
          console.error("Error creating mandatory documents:", docsError);
          // Don't throw - vehicle was created successfully, documents can be initialized later
        }
      }

      return data as Vehicle;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      toast.success("Véhicule ajouté avec succès");
    },
    onError: (error) => {
      console.error("Error creating vehicle:", error);
      toast.error("Erreur lors de l'ajout du véhicule");
    },
  });

  const updateVehicle = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Vehicle> & { id: string }) => {
      const { data, error } = await supabase
        .from("vehicles")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as Vehicle;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      toast.success("Véhicule mis à jour");
    },
    onError: (error) => {
      console.error("Error updating vehicle:", error);
      toast.error("Erreur lors de la mise à jour");
    },
  });

  const deleteVehicle = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("vehicles")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      toast.success("Véhicule supprimé");
    },
    onError: (error) => {
      console.error("Error deleting vehicle:", error);
      toast.error("Erreur lors de la suppression");
    },
  });

  return {
    vehicles,
    isLoading,
    error,
    createVehicle,
    updateVehicle,
    deleteVehicle,
  };
}

export function useVehicleDocuments(vehicleId: string | undefined) {
  const queryClient = useQueryClient();

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ["vehicle-documents", vehicleId],
    queryFn: async () => {
      if (!vehicleId) return [];

      const { data, error } = await supabase
        .from("vehicle_documents")
        .select(`
          *,
          linked_document:documents(id, title, file_name, file_path)
        `)
        .eq("vehicle_id", vehicleId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return data as VehicleDocument[];
    },
    enabled: !!vehicleId,
  });

  const linkDocument = useMutation({
    mutationFn: async ({ docId, documentId }: { docId: string; documentId: string }) => {
      const { error } = await supabase
        .from("vehicle_documents")
        .update({ 
          document_id: documentId,
          status: "valid" as ComplianceStatus
        })
        .eq("id", docId);

      if (error) throw error;
      
      // Recalculate compliance
      if (vehicleId) {
        await supabase.rpc("calculate_vehicle_compliance", { p_vehicle_id: vehicleId });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicle-documents", vehicleId] });
      queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      toast.success("Document lié avec succès");
    },
    onError: (error) => {
      console.error("Error linking document:", error);
      toast.error("Erreur lors de la liaison");
    },
  });

  const addDocument = useMutation({
    mutationFn: async (doc: Omit<VehicleDocument, "id" | "created_at" | "updated_at" | "linked_document">) => {
      // Use RPC to avoid enum vs text comparison issues
      const { data, error } = await supabase.rpc("upsert_vehicle_document_link", {
        p_vehicle_id: doc.vehicle_id,
        p_doc_type: doc.doc_type,
        p_document_id: doc.document_id || null,
        p_valid_from: doc.valid_from || null,
        p_expires_at: doc.expires_at || null,
        p_status: doc.status || "valid",
        p_label: doc.label || null,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicle-documents", vehicleId] });
      queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      toast.success("Document ajouté");
    },
  });

  const deleteDocument = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("vehicle_documents")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicle-documents", vehicleId] });
      toast.success("Document supprimé");
    },
  });

  // Initialize mandatory documents for a vehicle
  const initializeDocuments = useMutation({
    mutationFn: async (category: VehicleCategory) => {
      if (!vehicleId) throw new Error("No vehicle ID");

      const requiredDocs = getRequiredDocs(category);
      
      // Check existing types to avoid duplicates
      const { data: existing } = await supabase
        .from("vehicle_documents")
        .select("doc_type")
        .eq("vehicle_id", vehicleId);

      const existingTypes = new Set(existing?.map(d => d.doc_type) || []);
      const missingDocs = requiredDocs.filter(type => !existingTypes.has(type));

      if (missingDocs.length === 0) {
        throw new Error("Tous les documents obligatoires existent déjà");
      }

      const docsToCreate = missingDocs.map(docType => ({
        vehicle_id: vehicleId,
        doc_type: docType,
        label: VEHICLE_DOC_TYPES[docType].label,
        status: "missing" as ComplianceStatus,
      }));

      const { error } = await supabase.from("vehicle_documents").insert(docsToCreate);
      if (error) throw error;

      return missingDocs.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ["vehicle-documents", vehicleId] });
      toast.success(`${count} document(s) obligatoire(s) initialisé(s)`);
    },
    onError: (error: Error) => {
      console.error("Error initializing documents:", error);
      toast.error(error.message || "Erreur lors de l'initialisation");
    },
  });

  const unlinkDocument = useMutation({
    mutationFn: async (docId: string) => {
      const { error } = await supabase
        .from("vehicle_documents")
        .update({ document_id: null, status: "missing" as ComplianceStatus })
        .eq("id", docId);

      if (error) throw error;
      
      // Recalculate compliance
      if (vehicleId) {
        await supabase.rpc("calculate_vehicle_compliance", { p_vehicle_id: vehicleId });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicle-documents", vehicleId] });
      queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      toast.success("Document délié");
    },
    onError: (error) => {
      console.error("Error unlinking document:", error);
      toast.error("Erreur lors de la suppression du lien");
    },
  });

  return {
    documents,
    isLoading,
    linkDocument,
    unlinkDocument,
    addDocument,
    deleteDocument,
    initializeDocuments,
  };
}

export function useDrivers() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: drivers = [], isLoading } = useQuery({
    queryKey: ["drivers", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from("drivers")
        .select("*")
        .eq("user_id", user.id)
        .order("last_name", { ascending: true });

      if (error) throw error;
      return data as Driver[];
    },
    enabled: !!user?.id,
  });

  const createDriver = useMutation({
    mutationFn: async (driver: Omit<Driver, "id" | "user_id" | "access_token" | "created_at" | "invitation_sent_at" | "portal_last_access_at">) => {
      if (!user?.id) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("drivers")
        .insert({
          ...driver,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as Driver;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
      toast.success("Conducteur ajouté");
    },
    onError: (error) => {
      console.error("Error creating driver:", error);
      toast.error("Erreur lors de l'ajout du conducteur");
    },
  });

  const updateDriver = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Driver> & { id: string }) => {
      const { data, error } = await supabase
        .from("drivers")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as Driver;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
      toast.success("Conducteur mis à jour");
    },
    onError: (error) => {
      console.error("Error updating driver:", error);
      toast.error("Erreur lors de la mise à jour");
    },
  });

  const deleteDriver = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("drivers")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
      toast.success("Conducteur supprimé");
    },
  });

  return {
    drivers,
    isLoading,
    createDriver,
    updateDriver,
    deleteDriver,
  };
}

// Hook for vehicle-driver assignments
export interface VehicleDriver {
  id: string;
  vehicle_id: string;
  driver_id: string;
  is_primary: boolean;
  assigned_at: string;
  unassigned_at: string | null;
  driver?: Driver;
}

export function useVehicleDrivers(vehicleId: string | undefined) {
  const queryClient = useQueryClient();

  const { data: assignments = [], isLoading } = useQuery({
    queryKey: ["vehicle-drivers", vehicleId],
    queryFn: async () => {
      if (!vehicleId) return [];

      const { data, error } = await supabase
        .from("vehicle_drivers")
        .select(`
          *,
          driver:drivers(*)
        `)
        .eq("vehicle_id", vehicleId)
        .is("unassigned_at", null)
        .order("assigned_at", { ascending: false });

      if (error) throw error;
      return data as VehicleDriver[];
    },
    enabled: !!vehicleId,
  });

  const assignDriver = useMutation({
    mutationFn: async ({ driverId, isPrimary = false }: { driverId: string; isPrimary?: boolean }) => {
      if (!vehicleId) throw new Error("No vehicle ID");

      // If setting as primary, first unset any existing primary
      if (isPrimary) {
        await supabase
          .from("vehicle_drivers")
          .update({ is_primary: false })
          .eq("vehicle_id", vehicleId)
          .is("unassigned_at", null);
      }

      const { data, error } = await supabase
        .from("vehicle_drivers")
        .insert({
          vehicle_id: vehicleId,
          driver_id: driverId,
          is_primary: isPrimary,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicle-drivers", vehicleId] });
      toast.success("Conducteur assigné");
    },
    onError: (error) => {
      console.error("Error assigning driver:", error);
      toast.error("Erreur lors de l'assignation");
    },
  });

  const unassignDriver = useMutation({
    mutationFn: async (driverId: string) => {
      if (!vehicleId) throw new Error("No vehicle ID");

      const { error } = await supabase
        .from("vehicle_drivers")
        .update({ unassigned_at: new Date().toISOString() })
        .eq("vehicle_id", vehicleId)
        .eq("driver_id", driverId)
        .is("unassigned_at", null);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicle-drivers", vehicleId] });
      toast.success("Conducteur retiré");
    },
    onError: (error) => {
      console.error("Error unassigning driver:", error);
      toast.error("Erreur lors du retrait");
    },
  });

  const setPrimaryDriver = useMutation({
    mutationFn: async (driverId: string) => {
      if (!vehicleId) throw new Error("No vehicle ID");

      // Unset all primaries first
      await supabase
        .from("vehicle_drivers")
        .update({ is_primary: false })
        .eq("vehicle_id", vehicleId)
        .is("unassigned_at", null);

      // Set new primary
      const { error } = await supabase
        .from("vehicle_drivers")
        .update({ is_primary: true })
        .eq("vehicle_id", vehicleId)
        .eq("driver_id", driverId)
        .is("unassigned_at", null);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicle-drivers", vehicleId] });
      toast.success("Conducteur principal défini");
    },
  });

  // Get assigned driver IDs for easy checking
  const assignedDriverIds = assignments.map((a) => a.driver_id);

  return {
    assignments,
    assignedDriverIds,
    isLoading,
    assignDriver,
    unassignDriver,
    setPrimaryDriver,
  };
}
